<template>
  <div>
    <div class="flex items-center mb-6">
      <h1 class="font-bold text-3xl">
        <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('contacts.index')">Contacts</inertia-link>
        <span class="text-indigo-400 font-medium">/</span>
        {{ this.contactName }}
      </h1>
    </div>
    <trashed-message v-if="contact.data.deleted_at" class="mb-6" @restore="restore">
      This contact has been deleted.
    </trashed-message>

    <div class="w-full">
      <div class="bg-white rounded-md shadow overflow-hidden mb-8">
        <div class="px-8 py-4 bg-gray-50 border-b border-gray-100 flex items-center">
          <button v-if="!contact.data.deleted_at && this.can.delete" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">Delete Contact</button>
          <loading-button v-if="can.edit" :loading="form.processing" class="btn-indigo ml-auto" type="button" @click="this.update">Update Contact</loading-button>
        </div>
        <div class="p-8 -mr-6 -mb-4 flex flex-wrap">
          <div class="w-2/12">
            <img v-if="uploads.photo" :src="route('download-media.show', uploads.photo.uuid)" class="h-40" />
          </div>
          <div class="w-5/12">
            <div v-show="form.contact.honorific" class="pr-6 pb-2 w-full"><span class="font-semibold block">Honorific:</span>{{ form.contact.honorific }}</div>
            <div v-show="form.contact.salutation" class="pr-6 pb-2 w-full"><span class="font-semibold block">Salutation:</span>{{ form.contact.salutation }}</div>
            <div v-show="form.contact.given_name" class="pr-6 pb-2 w-full"><span class="font-semibold block">Given Name:</span>{{ form.contact.given_name }}</div>
            <div v-show="form.contact.surname" class="pr-6 pb-2 w-full"><span class="font-semibold block">Surname:</span>{{ form.contact.surname }}</div>
          </div>
          <div class="w-5/12">
            <div class="pr-6 pb-2 w-full"><span class="font-semibold block">Contact ID:</span>
                {{ this.contact.data.id }}
            </div>
            <div class="pr-6 pb-2 w-full"><span class="font-semibold block">Last Updated By:</span>{{ activity.causer }}</div>
            <div class="pr-6 pb-2 w-full"><span class="font-semibold block">Last Updated At:</span>
              <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('activity.show', [activity.id])">
                {{ dateFormat(activity.time) }}
              </inertia-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-md shadow overflow-hidden mb-8">
      <div class="bg-gray-50 border-b border-gray-100 flex items-center px-8 pt-4">
        <button @click="setActiveTab('contact')"  :class="tabClass('contact')" tabindex="-1" type="button">
          Contact
        </button>
        <button @click="setActiveTab('conference_arrangements')"  :class="tabClass('conference_arrangements')" tabindex="-1" type="button">
          Conference Arrangements
        </button>
        <button @click="setActiveTab('uploads')" :class="tabClass('uploads')" tabindex="-1" type="button">
          Uploads
        </button>
        <button @click="setActiveTab('events')" :class="tabClass('events')" tabindex="-1" type="button">
          Events
        </button>
      </div>
      <form @submit.prevent="update">
        <!-- TAB 1: Contact Information -->
        <div v-show="isTabActive('contact')" class="p-8 -mr-6 -mb-4 grid grid-cols-1 lg:grid-cols-3">
            <div>
              <div class="pr-6 mb-4 font-semibold ml-auto lg:w-2/3 text-center">Personal Information</div>
              <text-input v-model="form.contact.honorific" :error="formErrors('contact', 'honorific')" :inline="true" class="pr-6 pb-2" label="Honorific" />
              <text-input v-model="form.contact.salutation" :error="formErrors('contact', 'salutation')" :inline="true" class="pr-6 pb-2" label="Salutation" />
              <text-input v-model="form.contact.surname" :error="formErrors('contact', 'surname')" :inline="true" class="pr-6 pb-2" label="Surname" />
              <text-input v-model="form.contact.given_name" :error="formErrors('contact', 'given_name')" :inline="true" class="pr-6 pb-2" label="Given Name" />
              <text-input v-model="form.contact.middle_name" :error="formErrors('contact', 'middle_name')" :inline="true" class="pr-6 pb-2" label="Middle Name" />
              <text-input v-model="form.contact.suffix" :error="formErrors('contact', 'suffix')" :inline="true" class="pr-6 pb-2" label="Suffix" />
              <text-input v-model="form.contact.program_full_name" :error="formErrors('contact', 'program_full_name')" :inline="true" class="pr-6 pb-2" label="Full Name On Program" />
              <select-input v-model="form.contact.gender" :error="formErrors('contact', 'gender')" :inline="true" class="pr-6 pb-2" label="Gender">
                <option :value="null" />
                <option v-for="e in enums.GenderEnum" :key="e.value" :value="e.value" v-text="e.label"></option>
              </select-input>
              <text-input v-model="form.contact.nationality" :error="formErrors('contact', 'nationality')" :inline="true" class="pr-6 pb-2" label="Nationality" />
              <text-input v-model="form.contact.religious_affiliation" :error="formErrors('contact', 'religious_affiliation')" :inline="true" class="pr-6 pb-2" label="Religious Affiliation" />
              <text-input v-model="form.contact.religious_position" :error="formErrors('contact', 'religious_position')" :inline="true" class="pr-6 pb-2" label="Religious Position" />
              <text-input v-model="form.contact.job_title" :error="formErrors('contact', 'job_title')" :inline="true" class="pr-6 pb-2" label="Job Title" />
              <text-input v-model="form.contact.institution_name" :error="formErrors('contact', 'institution_name')" :inline="true" class="pr-6 pb-2" label="Institution Name" />
              <text-input v-model="form.contact.institution_acronym" :error="formErrors('contact', 'institution_acronym')" :inline="true" class="pr-6 pb-2" label="Institution Acronym" />
              <select-input v-model="form.contact.country_code" :error="formErrors('contact', 'country_code')" :inline="true" class="pr-6 pb-2" label="Country Code">
                <option :value="null" />
                <option v-for="country in countryList" :key="country.alpha2" :value="country.alpha2">{{ country.alpha2 }} &ndash; {{ country.name }}</option>
              </select-input>
              
              <div class="pr-6 mb-4 font-semibold ml-auto lg:w-2/3 text-center">Contact Information</div>
              <text-input v-model="form.contact.work_phone" :error="formErrors('contact', 'work_phone')" :inline="true" class="pr-6 pb-2" label="Work Phone" />
              <text-input v-model="form.contact.work_fax" :error="formErrors('contact', 'work_fax')" :inline="true" class="pr-6 pb-2" label="Work Fax" />
              <text-input v-model="form.contact.home_phone" :error="formErrors('contact', 'home_phone')" :inline="true" class="pr-6 pb-2" label="Home Phone" />
              <text-input v-model="form.contact.mobile_phone" :error="formErrors('contact', 'mobile_phone')" :inline="true" class="pr-6 pb-2" label="Mobile Phone" />
              <text-input v-model="form.contact.whatsapp_number" :error="formErrors('contact', 'whatsapp_number')" :inline="true" class="pr-6 pb-2" label="WhatsApp Number" />
              <text-input v-model="form.contact.primary_email" :error="formErrors('contact', 'primary_email')" type="email" :inline="true" class="pr-6 pb-2" label="Email" />
              <text-input v-model="form.contact.alternate_email" :error="formErrors('contact', 'alternate_email')" type="email" :inline="true" class="pr-6 pb-2" label="Alternate Email" />
            </div>
            <div>
              <div class="pr-6 mb-4 font-semibold ml-auto lg:w-2/3 text-center">Mailing Address</div>
              <textarea-input v-model="form.contact.mailing_address_line_1" :error="formErrors('contact', 'mailing_address_line_1')" :inline="true" rows="1" class="pr-6 pb-2" label="Line 1" />
              <text-input v-model="form.contact.mailing_address_line_2" :error="formErrors('contact', 'mailing_address_line_2')" :inline="true" class="pr-6 pb-2" label="Line 2" />
              <text-input v-model="form.contact.mailing_address_line_3" :error="formErrors('contact', 'mailing_address_line_3')" :inline="true" class="pr-6 pb-2" label="Line 3" />
              <text-input v-model="form.contact.mailing_city" :error="formErrors('contact', 'mailing_city')" :inline="true" class="pr-6 pb-2" label="City" />
              <text-input v-model="form.contact.mailing_state" :error="formErrors('contact', 'mailing_state')" :inline="true" class="pr-6 pb-2" label="State/Province" />
              <text-input v-model="form.contact.mailing_postal_code" :error="formErrors('contact', 'mailing_postal_code')" :inline="true" class="pr-6 pb-2" label="ZIP/Postal Code" />
              <text-input v-model="form.contact.mailing_country" :error="formErrors('contact', 'mailing_country')" :inline="true" class="pr-6 pb-2" label="Country" />
              
              <div class="pr-6 mb-4 font-semibold ml-auto lg:w-2/3 text-center">Residence Address</div>
              <textarea-input v-model="form.contact.residence_address_line_1" :error="formErrors('contact', 'residence_address_line_1')" :inline="true" rows="1" class="pr-6 pb-2" label="Line 1" />
              <text-input v-model="form.contact.residence_address_line_2" :error="formErrors('contact', 'residence_address_line_2')" :inline="true" class="pr-6 pb-2" label="Line 2" />
              <text-input v-model="form.contact.residence_address_line_3" :error="formErrors('contact', 'residence_address_line_3')" :inline="true" class="pr-6 pb-2" label="Line 3" />
              <text-input v-model="form.contact.residence_city" :error="formErrors('contact', 'residence_city')" :inline="true" class="pr-6 pb-2" label="City" />
              <text-input v-model="form.contact.residence_state" :error="formErrors('contact', 'residence_state')" :inline="true" class="pr-6 pb-2" label="State/Province" />
              <text-input v-model="form.contact.residence_postal_code" :error="formErrors('contact', 'residence_postal_code')" :inline="true" class="pr-6 pb-2" label="ZIP/Postal Code" />
              <text-input v-model="form.contact.residence_country" :error="formErrors('contact', 'residence_country')" :inline="true" class="pr-6 pb-2" label="Country" />
            </div>
            <div>
              <div class="pr-6 mb-4 font-semibold ml-auto lg:w-2/3 text-center">Assistant Information</div>
              <text-input v-model="form.contact.assistant_name" :error="formErrors('contact', 'assistant_name')" :inline="true" class="pr-6 pb-2" label="Name" />
              <text-input v-model="form.contact.assistant_work_phone" :error="formErrors('contact', 'assistant_work_phone')" :inline="true" class="pr-6 pb-2" label="Work Phone" />
              <text-input v-model="form.contact.assistant_home_phone" :error="formErrors('contact', 'assistant_home_phone')" :inline="true" class="pr-6 pb-2" label="Home Phone" />
              <text-input v-model="form.contact.assistant_mobile_phone" :error="formErrors('contact', 'assistant_mobile_phone')" :inline="true" class="pr-6 pb-2" label="Mobile Phone" />
              <text-input v-model="form.contact.assistant_email" :error="formErrors('contact', 'assistant_email')" type="email" :inline="true" class="pr-6 pb-2" label="Email" />
              
              <div class="pr-6 mb-4 font-semibold ml-auto lg:w-2/3 text-center">Notes</div>
              <select-input v-model="form.contact.contact_type_id" :error="formErrors('attendee', 'contact_type_id')" :inline="true" class="pr-6 pb-2" label="Contact Type">
                <option :value="null" />
                <option v-for="(name, id) in contactTypes" :key="id" :value="id" v-text="name"></option>
              </select-input>
              <text-input v-model="form.contact.owner" :error="formErrors('contact', 'owner')" :inline="true" class="pr-6 pb-2" label="Contact Owner" />
              <text-input v-model="form.contact.authorized_user" :error="formErrors('contact', 'authorized_user')" :inline="true" class="pr-6 pb-2" label="Authorized User" />
              <text-input v-model="form.contact.data_source" :error="formErrors('contact', 'data_source')" :inline="true" class="pr-6 pb-2" label="Data Source" />
              <textarea-input v-model="form.contact.other_events_attended" :error="formErrors('contact', 'other_events_attended')" :inline="true" class="pr-6 pb-2" label="Other Events Attended" />
              <textarea-input v-model="form.contact.additional_info" :error="formErrors('contact', 'additional_info')" :inline="true" class="pr-6 pb-2" label="Additional Info" />
              <textarea-input v-model="form.contact.biographical_sketch" :error="formErrors('contact', 'biographical_sketch')" :inline="true" class="pr-6 pb-2" label="Biographical Sketch" />
              <textarea-input v-model="form.contact.conference_biography" :error="formErrors('contact', 'conference_biography')" :inline="true" class="pr-6 pb-2" label="Conference Biography" />
              <checkbox-input v-model="form.contact.current_member" :error="formErrors('contact', 'current_member')" :inline="true" class="pr-6 pb-2" label="Current Member" />
              <text-input v-model="form.contact.latest_member_year" :error="formErrors('contact', 'latest_member_year')" :inline="true" class="pr-6 pb-2" label="Latest Member Year" />
              <checkbox-input v-model="form.contact.subscribed" :error="formErrors('contact', 'subscribed')" :inline="true" class="pr-6 pb-2" label="Subscribed" />
              <text-input :value="dateFormat(form.contact.subscribed_updated_at)" disabled :inline="true" class="pr-6 pb-2" label="Last Updated" />
              <checkbox-input v-model="form.contact.data_permissions" :error="formErrors('contact', 'data_permissions')" :inline="true" class="pr-6 pb-2" label="Data Permissions" />
              <text-input :value="dateFormat(form.contact.data_permissions_updated_at)" disabled :inline="true" class="pr-6 pb-2" label="Last Updated" />
            </div>
        </div>

        <!-- TAB 2: Conference Arrangements -->
        <div v-show="isTabActive('conference_arrangements')" class="p-8 -mr-6 -mb-4 grid grid-cols-1 lg:grid-cols-3">
          <div>
            <div class="pr-6 mb-4 font-semibold ml-auto lg:w-2/3 text-center">Personal Information</div>
            <text-input v-model="form.contact.surname" :error="formErrors('contact', 'surname')" :inline="true" class="pr-6 pb-2" label="Surname" />
            <text-input v-model="form.contact.given_name" :error="formErrors('contact', 'given_name')" :inline="true" class="pr-6 pb-2" label="Given Name" />
            <text-input v-model="form.contact.program_full_name" :error="formErrors('contact', 'program_full_name')" :inline="true" class="pr-6 pb-2" label="Full Name on Program" />
            <select-input v-model="form.contact.gender" :error="formErrors('contact', 'gender')" :inline="true" class="pr-6 pb-2" label="Gender">
              <option :value="null" />
              <option v-for="e in enums.GenderEnum" :key="e.value" :value="e.value" v-text="e.label"></option>
            </select-input>
            <date-input v-model="form.contact.birthdate" :error="formErrors('contact', 'birthdate')" type="date" :inline="true" class="pr-6 pb-2" label="Birthdate" />
            <textarea-input v-model="form.contact.birthdate_notes" :error="formErrors('contact', 'birthdate_notes')" :inline="true" rows="1" class="pr-6 pb-2" label="Birthdate Notes" />
            <text-input v-model="form.contact.birthplace" :error="formErrors('contact', 'birthplace')" :inline="true" class="pr-6 pb-2" label="Birthplace" />
            <text-input v-model="form.contact.religious_affiliation" :error="formErrors('contact', 'religious_affiliation')" :inline="true" class="pr-6 pb-2" label="Religious Affiliation" />
            <text-input v-model="form.contact.religious_position" :error="formErrors('contact', 'religious_position')" :inline="true" class="pr-6 pb-2" label="Religious Position" />
            <text-input v-model="form.contact.nationality" :error="formErrors('contact', 'nationality')" :inline="true" class="pr-6 pb-2" label="Nationality" />

            <div class="pr-6 mb-4 font-semibold ml-auto lg:w-2/3 text-center">Language</div>
            <text-input v-model="form.contact.primary_language" :error="formErrors('contact', 'primary_language')" :inline="true" class="pr-6 pb-2" label="Primary Language" />
            <select-input v-model="form.contact.english_fluency" :error="formErrors('contact', 'english_fluency')" :inline="true" class="pr-6 pb-2" label="English Fluency">
              <option :value="null" />
              <option v-for="e in enums.EnglishFluencyEnum" :key="e.value" :value="e.value" v-text="e.label"></option>
            </select-input>
            <text-input v-model="form.contact.other_languages_spoken" :error="formErrors('contact', 'other_languages_spoken')" :inline="true" class="pr-6 pb-2" label="Other Languages" />
          </div>
          <div>
            <div class="pr-6 mb-4 font-semibold ml-auto lg:w-2/3 text-center">Passport</div>
            <text-input v-model="form.contact.passport_full_name" :error="formErrors('contact', 'passport_full_name')" :inline="true" class="pr-6 pb-2" label="Full Name" />
            <text-input v-model="form.contact.passport_number" :error="formErrors('contact', 'passport_number')" :inline="true" class="pr-6 pb-2" label="Number" />
            <date-input v-model="form.contact.passport_issue_date" :error="formErrors('contact', 'passport_issue_date')" type="date" :inline="true" class="pr-6 pb-2" label="Issue Date" />
            <textarea-input v-model="form.contact.passport_issue_date_notes" :error="formErrors('contact', 'passport_issue_date_notes')" :inline="true" rows="1" class="pr-6 pb-2" label="Issue Date Notes" />
            <date-input v-model="form.contact.passport_expiration_date" :error="formErrors('contact', 'passport_expiration_date')" type="date" :inline="true" class="pr-6 pb-2" label="Expiration Date" />
            <textarea-input v-model="form.contact.passport_expiration_date_notes" :error="formErrors('contact', 'passport_expiration_date_notes')" :inline="true" rows="1" class="pr-6 pb-2" label="Expiration Date Notes" />
            <text-input v-model="form.contact.passport_issuer" :error="formErrors('contact', 'passport_issuer')" :inline="true" class="pr-6 pb-2" label="Issuer" />
            <text-input v-model="form.contact.passport_issue_place" :error="formErrors('contact', 'passport_issue_place')" :inline="true" class="pr-6 pb-2" label="Place of Issue" />
            <checkbox-input v-model="form.contact.passport_scan" :error="formErrors('contact', 'passport_scan')" :inline="true" class="pr-6 pb-2" label="Scan" />

            <div class="pr-6 mb-4 font-semibold ml-auto lg:w-2/3 text-center">Travel Information</div>
            <text-input v-model="form.contact.preferred_airline" :error="formErrors('contact', 'preferred_airline')" :inline="true" class="pr-6 pb-2" label="Preferred Airline" />
            <text-input v-model="form.contact.preferred_seating" :error="formErrors('contact', 'preferred_seating')" :inline="true" class="pr-6 pb-2" label="Preferred Seating" />
            <text-input v-model="form.contact.frequent_flyer_number" :error="formErrors('contact', 'frequent_flyer_number')" :inline="true" class="pr-6 pb-2" label="Frequent Flyer Number" />
          </div>
          <div>
            <div class="pr-6 mb-4 font-semibold ml-auto lg:w-2/3 text-center">Registration</div>
            <textarea-input v-model="form.contact.staff_notes" :error="formErrors('contact', 'staff_notes')" :inline="true" class="pr-6 pb-2" label="Staff Notes" />
            <textarea-input v-model="form.contact.special_needs" :error="formErrors('contact', 'special_needs')" :inline="true" class="pr-6 pb-2" label="Special Needs" />
            <textarea-input v-model="form.contact.other_dietary_needs" :error="formErrors('contact', 'other_dietary_needs')" :inline="true" class="pr-6 pb-2" label="Dietary Needs" />
          </div>
        </div>

        <!-- TAB 3: Uploads -->
        <div v-show="isTabActive('uploads')" class="p-8 -mr-6 -mb-4">
          <file-input v-model="form.uploads.photo" :error="formErrors('uploads', 'photo')" class="pr-6 pb-2 pt-4 w-full lg:w-1/2" type="file" label="Photo" />
          <a v-if="uploads.photo" class="text-indigo-400 hover:text-indigo-600" :href="route('download-media.show', uploads.photo.uuid)" download>{{ uploads.photo.file_name }}</a>

          <file-input v-model="form.uploads.personal_biography" :error="formErrors('uploads', 'personal_biography')" class="pr-6 pb-2 pt-4 w-full lg:w-1/2" type="file" label="Personal Biography" />
          <a v-if="uploads.personal_biography" class="text-indigo-400 hover:text-indigo-600" :href="route('download-media.show', uploads.personal_biography.uuid)" download>{{ uploads.personal_biography.file_name }}</a>

          <file-input v-model="form.uploads.passport" :error="formErrors('uploads', 'passport')" class="pr-6 pb-2 pt-4 w-full lg:w-1/2" type="file" label="Passport" />
          <a v-if="uploads.passport" class="text-indigo-400 hover:text-indigo-600" :href="route('download-media.show', uploads.passport.uuid)" download>{{ uploads.passport.file_name }}</a>
        </div>

        <!-- TAB 4: Events -->
        <div v-show="isTabActive('events')" class="p-8 -mr-6 -mb-4">
          <ul class="list-disc list-inside">
            <li v-for="(eventName, attendeeId) in events" :key="attendeeId">
              <a class="text-indigo-400 hover:text-indigo-600" :href="route('attendees.edit', [attendeeId])" v-text="eventName"></a>
            </li>
          </ul>
          <span v-if="events.length === 0">No Events Found</span>
        </div>

        <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center">
          <button v-if="!contact.data.deleted_at && this.can.delete" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">Delete Contact</button>
          <loading-button v-if="can.edit" :loading="form.processing" class="btn-indigo ml-auto" type="submit">Update Contact</loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import Icon from "@/Shared/Icon";
import TextInput from '@/Shared/TextInput'
import DateInput from '@/Shared/DateInput'
import TextareaInput from '@/Shared/TextareaInput'
import SelectInput from '@/Shared/SelectInput'
import CheckboxInput from '@/Shared/CheckboxInput'
import FileInput from '@/Shared/FileInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import * as enums from '@/Enums/Enums'
import { DateTime } from "luxon"
import { cloneDeep } from "lodash"
import { iso31661 } from 'iso-3166'

export default {
  metaInfo() {
    return { title: this.contactName }
  },
  components: {
    Icon,
    SelectInput,
    TextInput,
    TextareaInput,
    CheckboxInput,
    FileInput,
    TrashedMessage,
    LoadingButton,
    DateInput,
  },
  props: {
    contact: Object,
    activity: Object,
    uploads: Object,
    contactTypes: Object,
    can: Object,
    events: Object,
  },
  layout: Layout,
  data() {
    return {
      tab: 'contact',
      form: this.$inertia.form({
        _method: 'put',
        contact: {},
        uploads: {
          photo: null,
          personal_biography: null,
          passport: null,
        }
      }),
    }
  },
  mounted() {
    this.loadData();
  },
  computed: {
    contactName() {
      return this.formatName(this.form.contact);
    },
    countryList() {
      return iso31661;
    },
    enums() {
      return enums
    },
  },
  methods: {
    loadData() {
      this.form.contact = cloneDeep(this.contact.data);
    },
    update() {
      this.form.post(this.route('contacts.update', this.contact.data.id), {
        onSuccess: () => {
          this.form.reset();
          this.loadData();
        },
      })
    },
    destroy() {
      if (confirm('Are you sure you want to delete this contact?')) {
        this.$inertia.delete(this.route('contacts.destroy', this.contact.data.id))
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this contact?')) {
        this.$inertia.put(this.route('contacts.restore', this.contact.data.id))
      }
    },
    dateFormat(iso) {
      if (iso === null) {
        return '';
      }
      
      return DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_FULL)
    },
    tabClass(tab) {
      if (this.isTabActive(tab)) {
        return ['flex', 'items-center', 'border-b-2', 'border-indigo-600', 'px-4', 'pb-3', 'font-semibold'];
      } else {
        return ['flex', 'items-center', 'border-b-2', 'border-transparent', 'hover:border-gray-400', 'transition', 'ease-in-out', 'duration-150', 'px-4 pb-3'];
      }
    },
    setActiveTab(newTab) {
      this.tab = newTab;
    },
    isTabActive(tab) {
      return this.tab === tab;
    },
    formErrors(...args) {
      return this.form.errors[args.join('.')];
    },
    formatName(contact) {
      if (contact.program_full_name) {
        return contact.program_full_name;
      } else if (contact.passport_full_name) {
        return contact.passport_full_name;
      } else if (contact.given_name || contact.surname) {
        return contact.given_name + " " + contact.surname;
      } else {
        return "[New Contact]";
      }
    },
  },
}
</script>
